import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api';
import { KeyStringFilterInput, StringFilterInput, IntFilterInput, BooleanFilterInput } from 'app/api/common.api.types';
import { EMPTY, from, Observable } from 'rxjs';
import { concatAll, expand, map, toArray } from 'rxjs/operators';
import {
  UpdateAnalyticsEntryInput,
  CreateAnalyticsEntryInput,
  DeleteAnalyticsEntryInput,
  GroupAnalyticsEntryInput,
  UpdateAnalyticsEntrySortInput
} from './API.service';

export interface AnalyticsFilterInput {
  key: StringFilterInput;
  name: StringFilterInput;
  description: StringFilterInput;
  sortOrder: IntFilterInput;
  parentKey: StringFilterInput;
  paymentPeriod: StringFilterInput;
  isCash: BooleanFilterInput;
  groupType: StringFilterInput;
  color: StringFilterInput;
}

export interface AnalyticsEntry {
  companyId: string; 
  key: string;
  axis: string;
  id: string;
  name: string;
  description?: string;
  sortOrder: number;
  parentKey?: string;
  groups?: string[];
  paymentPeriod?: string;
  isCash?: boolean; // only for ANA3
  groupType?: string;
  color?: string;
}

export interface AnalyticsConnection {
  items: AnalyticsEntry[];
  lastEvaluatedKey: any;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsAPIService {
  CreateAnalyticsEntry(
    input: CreateAnalyticsEntryInput
  ): Observable<AnalyticsEntry> {
    const statement = `mutation CreateAnalyticsEntry($input: CreateAnalyticsEntryInput!) {
      createAnalyticsEntry(input: $input) {
        companyId
        key
        axis
        id
        name
        description
        sortOrder
        parentKey
        groups
        paymentPeriod
        isCash
        groupType
        color
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };

    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any): AnalyticsEntry => {
        return response.data.createAnalyticsEntry;
      })
    );
  }

  UpdateAnalyticsEntrySort(
    input: Array<UpdateAnalyticsEntrySortInput>
  ): Observable<boolean | null> {
    const statement = `mutation UpdateAnalyticsEntrySort($input: [UpdateAnalyticsEntrySortInput!]!) {
        updateAnalyticsEntrySort(input: $input)
      }`;
    const gqlAPIServiceArguments: any = {
      input
    };
    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any): (boolean | null) => {
        return response.data.updateAnalyticsEntrySort;
      })
    );
  }

  UpdateAnalyticsEntry(
    input: UpdateAnalyticsEntryInput
  ): Observable<AnalyticsEntry> {
    const statement = `mutation UpdateAnalyticsEntry($input: UpdateAnalyticsEntryInput!) {
      updateAnalyticsEntry(input: $input) {
        companyId
        key
        axis
        id
        name
        description
        sortOrder
        parentKey
        groups
        paymentPeriod
        isCash
        groupType
        color
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };
    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any): AnalyticsEntry => {
        return response.data.updateAnalyticsEntry;
      })
    );
  }

  DeleteAnalyticsEntry(
    input: DeleteAnalyticsEntryInput
  ): Observable<AnalyticsEntry> {
    const statement = `mutation DeleteAnalyticsEntry($input: DeleteAnalyticsEntryInput!) {
      deleteAnalyticsEntry(input: $input) {
        companyId
        key
        axis
        id
        name
        description
        sortOrder
        parentKey
        groups
        paymentPeriod
        isCash
        groupType
        color
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };

    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any): AnalyticsEntry => {
        return response.data.deleteAnalyticsEntry;
      })
    );
  }

  AddGroupAnalyticsEntry(
    input: GroupAnalyticsEntryInput
  ): Observable<AnalyticsEntry> {
    const statement = `mutation AddGroupAnalyticsEntry($input: GroupAnalyticsEntryInput!) {
      addGroupAnalyticsEntry(input: $input) {
        companyId
        key
        axis
        id
        name
        description
        sortOrder
        parentKey
        groups
        paymentPeriod
        isCash
        groupType
        color
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };

    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any): AnalyticsEntry => {
        return response.data.addGroupAnalyticsEntry;
      })
    );
  }

  RemoveGroupAnalyticsEntry(
    input: GroupAnalyticsEntryInput
  ): Observable<AnalyticsEntry> {
    const statement = `mutation RemoveGroupAnalyticsEntry($input: GroupAnalyticsEntryInput!) {
      removeGroupAnalyticsEntry(input: $input) {
        companyId
        key
        axis
        id
        name
        description
        sortOrder
        parentKey
        groups
        paymentPeriod
        isCash
        groupType
        color
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };

    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any): AnalyticsEntry => {
        return response.data.removeGroupAnalyticsEntry;
      })
    );
  }

  InitAnalyticsEntries(
    companyId: string
  ): Observable<AnalyticsEntry[]> {
    const statement = `query InitAnalyticsEntries($companyId: String!) {
      initAnalyticsEntries(companyId: $companyId) {
        items {
          companyId
          key
          axis
          id
          name
          description
          sortOrder
          parentKey
          groups
          paymentPeriod
          isCash
          groupType
          color
        }
      }
    }`;
    const gqlAPIServiceArguments: any = {
      companyId
    };
    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any): AnalyticsEntry[] => {
        return response.data.initAnalyticsEntries.items;
      })
    );
  }

  Analytics(
    companyId: string,
    key?: KeyStringFilterInput,
    filter?: AnalyticsFilterInput,
    limit?: number,
  ): Observable<AnalyticsEntry[]> {
    // tslint:disable-next-line: max-line-length
    const statement = `query Analytics($id: String!, $key: KeyStringFilterInput, $filter: AnalyticsFilterInput, $limit: Int, $exclusiveStartKey: AWSJSON) {
      company(id: $id) {
        analytics(key: $key, filter: $filter, limit: $limit, exclusiveStartKey: $exclusiveStartKey) {
          items {
            companyId
            key
            axis
            id
            name
            description
            sortOrder
            parentKey
            groups
            paymentPeriod
            isCash
            groupType
            color
          }
          lastEvaluatedKey
        }
      }
    }`;
    const gqlAPIServiceArguments: any = {};
    gqlAPIServiceArguments.id = companyId;
    if (key) {
      gqlAPIServiceArguments.key = key;
    }
    if (filter) {
      gqlAPIServiceArguments.filter = filter;
    }
    if (limit) {
      gqlAPIServiceArguments.limit = limit;
    }

    return this.performQuery(statement, gqlAPIServiceArguments)
      .pipe(
        expand(conn => {
          const exclusiveStartKey = conn.lastEvaluatedKey;
          const args = { ...gqlAPIServiceArguments, exclusiveStartKey };
          return (conn.lastEvaluatedKey && JSON.parse(conn.lastEvaluatedKey) !== '')
            ? this.performQuery(statement, args)
            : EMPTY;
        }),
        map(conn => conn.items),
        concatAll(),
        toArray()
      );
  }

  private performQuery(statement: string, args: any = {}): Observable<AnalyticsConnection> {
    return from(API.graphql(
      graphqlOperation(statement, args)
    )).pipe(
      map((response: any): AnalyticsConnection => {
        return response.data.company.analytics;
      })
    );
  }

}
